/**
 * Formulário da tela de Seguro de Carga: registro de sinistro.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CargoPolicyKind } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { IconX } from "../../components/icons";

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

function Dialog({ title, subtitle, onClose, children, id }: { title: string; subtitle?: string; onClose: () => void; children: ReactNode; id: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby={id}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-5 rounded-2xl bg-white p-6 shadow-card-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={id} className="text-base font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-sm text-gray-600">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1" aria-label="Fechar"><IconX className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ["cargo-insurance"] });

const KIND_OPTIONS: Array<{ key: CargoPolicyKind; label: string }> = [
  { key: "rc_dc", label: "RC-DC · roubo ou desaparecimento" },
  { key: "rctr_c", label: "RCTR-C · acidente" },
  { key: "rc_v", label: "RC-V · danos a terceiros" },
];

const parseMoney = (s: string): number | null => {
  const t = s.trim().replace(/\./g, "").replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export function ClaimDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<CargoPolicyKind>("rc_dc");
  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [plate, setPlate] = useState("");
  const [loss, setLoss] = useState("");
  const [indemnity, setIndemnity] = useState("");
  const [status, setStatus] = useState<"aberto" | "indenizado" | "negado">("aberto");
  const save = useMutation({
    mutationFn: () => api.post("/cargo-insurance/claims", {
      policyKind: kind, occurredAt: `${date}T12:00:00-03:00`, description, plate: plate || null,
      lossValue: parseMoney(loss), indemnityValue: parseMoney(indemnity), status,
    }),
    onSuccess: () => { void invalidate(qc); onClose(); },
  });
  return (
    <Dialog id="claim-title" title="Registrar sinistro" subtitle="Registre o prejuízo e o que a seguradora indenizou." onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-gray-600 sm:col-span-2">Apólice
            <select className="input mt-1 w-full" value={kind} onChange={(e) => setKind(e.target.value as CargoPolicyKind)}>
              {KIND_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-gray-600">Data
            <input type="date" required className="input mt-1 w-full" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-600">Placa
            <input className="input mt-1 w-full" value={plate} maxLength={20} onChange={(e) => setPlate(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-600 sm:col-span-2">O que aconteceu
            <textarea required minLength={3} maxLength={500} rows={2} className="input mt-1 w-full" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-600">Prejuízo (R$)
            <input inputMode="decimal" className="input mt-1 w-full" value={loss} onChange={(e) => setLoss(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-600">Indenização (R$)
            <input inputMode="decimal" className="input mt-1 w-full" value={indemnity} onChange={(e) => setIndemnity(e.target.value)} />
          </label>
          <label className="block text-xs font-medium text-gray-600 sm:col-span-2">Situação
            <select className="input mt-1 w-full" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="aberto">Em aberto</option>
              <option value="indenizado">Indenizado</option>
              <option value="negado">Negado</option>
            </select>
          </label>
        </div>
        {save.isError && <p className="text-sm text-danger-700">{errMsg(save.error, "Não consegui gravar. Tente de novo.")}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={save.isPending || description.trim().length < 3}>{save.isPending ? "Gravando…" : "Registrar"}</button>
        </div>
      </form>
    </Dialog>
  );
}
